/**
 * ClientCalendarUIContext
 * Manages UI state for client calendar (modals, dialogs)
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const ClientCalendarUIContext = createContext(null);

export const useClientCalendarUI = () => {
  const context = useContext(ClientCalendarUIContext);
  if (!context) {
    throw new Error('useClientCalendarUI must be used within ClientCalendarUIProvider');
  }
  return context;
};

export const ClientCalendarUIProvider = ({ children }) => {
  // Modal states
  const [modals, setModals] = useState({
    eventView: false,
  });

  // Selected event/task data
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventMetadata, setSelectedEventMetadata] = useState(null);

  // Day events popup state
  const [dayEventsPopup, setDayEventsPopup] = useState({
    visible: false,
    date: null,
  });

  // Open event view modal
  const openEventViewModal = useCallback((eventIdOrEvent, metadata = null) => {
    if (typeof eventIdOrEvent === 'string') {
      // If passed an ID string
      setSelectedEventId(eventIdOrEvent);
      setSelectedEventMetadata(metadata);
    } else if (eventIdOrEvent?.extendedProps?.sk) {
      // If passed an event object
      setSelectedEventId(eventIdOrEvent.extendedProps.sk);
      setSelectedEventMetadata({
        calendar_type: eventIdOrEvent.extendedProps?.calendar_type || eventIdOrEvent.calendar_type || 'event',
        res_type: eventIdOrEvent.extendedProps?.res_type || eventIdOrEvent.res_type || 'event',
      });
    }

    setModals((prev) => ({ ...prev, eventView: true }));
  }, []);

  // Close event view modal
  const closeEventViewModal = useCallback(() => {
    setModals((prev) => ({ ...prev, eventView: false }));
    // Clear selection after modal animation
    setTimeout(() => {
      setSelectedEventId(null);
      setSelectedEventMetadata(null);
    }, 300);
  }, []);

  // Open day events popup
  const openDayEventsPopup = useCallback((date) => {
    setDayEventsPopup({ visible: true, date });
  }, []);

  // Close day events popup
  const closeDayEventsPopup = useCallback(() => {
    setDayEventsPopup({ visible: false, date: null });
  }, []);

  // Memoize the context value
  const value = useMemo(
    () => ({
      // Modal states
      modals,

      // Selected event data
      selectedEventId,
      selectedEventMetadata,

      // Day events popup state
      dayEventsPopup,

      // Modal actions
      openEventViewModal,
      closeEventViewModal,
      openDayEventsPopup,
      closeDayEventsPopup,
    }),
    [
      modals,
      selectedEventId,
      selectedEventMetadata,
      dayEventsPopup,
      openEventViewModal,
      closeEventViewModal,
      openDayEventsPopup,
      closeDayEventsPopup,
    ]
  );

  return <ClientCalendarUIContext.Provider value={value}>{children}</ClientCalendarUIContext.Provider>;
};

export default ClientCalendarUIContext;
